import React, { useEffect, useState } from "react";
import { Button, Form, Input, Row, Col, Select, Divider } from "antd";
import { CheckOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { post } from "../../../../services/services";
import { ApiURL } from "../../../../services/apiConstants";
import { ErrorToast, SuccessToast } from "../../../../widgets/AppToast";

const OdsEquipment = ({
  mode = "add",
  odsId,
  onSuccess,
  equipmentDetails,
  readOnly = false,
  isSaved = false,
  onSaveSuccess,
}) => {
  const [form] = Form.useForm();
  const userData = JSON.parse(localStorage.getItem("user_data"));
  const shipId = userData?.data?.ship_details?.ship_id;
  const [systemTypes, setSystemTypes] = useState([]);
  const [newSystemType, setNewSystemType] = useState("");
  const [addingType, setAddingType] = useState(false);

  const fetchSystemTypes = async () => {
    post(ApiURL.ods_system_types, new FormData()).then((res) => {
      if (res?.status == true) {
        setSystemTypes(res.data);
      }
    });
  };



  const handleAddSystemType = async () => {
    if (!newSystemType.trim()) return;
    setAddingType(true);
    try {
      const fd = new FormData();
      fd.append("system_type", newSystemType);
      post(ApiURL.ods_system_types, fd).then((res) => {
        if (res?.status == true) {
          SuccessToast("Success", "System type added successfully");
          fetchSystemTypes();
          setNewSystemType("");
        }
      });
    } catch (error) {
      ErrorToast("Error", error?.message || "Failed to add system type");
    }

    setAddingType(false);
  };

  const onFinish = async (values) => {
    const fd = new FormData();

    if (mode === "edit") {
      fd.append("ods_id", odsId);
    } else {
      fd.append("ship_id", shipId);
    }

    Object.entries(values).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        fd.append(key, value);
      }
    });

    const api =
      mode === "edit" ? ApiURL.ods_equipment_update : ApiURL.ods_equipment;

    const res = await post(api, fd);

    const responseData = res?.response?.data || res;

    if (responseData?.status === true) {
      SuccessToast("Success", "ODS equipment saved successfully");

      // Notify parent that form is saved
      if (typeof onSaveSuccess === "function") {
        onSaveSuccess();
      }

      if (
        mode === "add" &&
        typeof onSuccess === "function" &&
        responseData?.data?.ods_id
      ) {
        onSuccess(responseData?.data?.ods_id);
      }
    } else {
      if (responseData?.errors) {
        const fieldErrors = Object.keys(responseData.errors).map((field) => ({
          name: field,
          errors: responseData?.errors[field],
        }));

        form.setFields(fieldErrors);
      }

      ErrorToast(
        "Error",
        responseData?.message || "Please fill all required fields",
      );
    }
  };

  useEffect(() => {
    if (equipmentDetails && systemTypes?.length) {
      const match = systemTypes.find(
        (i) =>
          i.name.toLowerCase() ===
          equipmentDetails.equipment_name.toLowerCase(),
      );

      form.setFieldsValue({
        equipment: match?.id,
        ...equipmentDetails,
      });
    }
  }, [equipmentDetails, systemTypes]);


  const handleDeleteSystemType = async (id) => {
    try {
      const fd = new FormData();
      fd.append("del", id);

      const res = await post(ApiURL.ods_system_types, fd);

      if (res?.status === true) {
        SuccessToast("Deleted", "System type deleted successfully");
        fetchSystemTypes();
      } else {
        ErrorToast("Error", res?.message || "Delete failed");
      }
    } catch (error) {
      ErrorToast("Error", error?.message || "Delete failed");
    }
  };

  useEffect(() => {
    fetchSystemTypes();
  }, []);

  return (
    <div className="white-container">
      <h3 className="section-title">ODS Equipment Log</h3>
      <Form 
        layout="vertical" 
        form={form} 
        onFinish={onFinish}
        onValuesChange={(changedValues) => {
          const fieldName = Object.keys(changedValues)[0];
          form.setFields([{ name: fieldName, errors: [] }]);
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item 
              label="Equipment Name" 
              name="equipment" 
              required
              rules={[{ required: true, message: "The equipment field is required." }]}
            >
              <Select
              disabled={readOnly}
                className="equipment-select"
                placeholder="Select or add equipment name"
                options={systemTypes.map((i) => ({
                  label: i.name,
                  value: i.id,
                }))}
                dropdownRender={() => (
                  <>
                    {systemTypes.map((item) => (
                      <div
                        key={item.id}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "8px 12px",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          form.setFieldsValue({ equipment: item.id })
                        }
                      >
                        <span>{item.name}</span>

                        <DeleteOutlined
                          style={{ color: "red" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSystemType(item.id);
                          }}
                        />
                      </div>
                    ))}

                    <>
                      <Divider />
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          padding: "0 8px 8px",
                        }}
                      >
                        <Input
                          placeholder="Add new equipment"
                          value={newSystemType}
                          onChange={(e) => setNewSystemType(e.target.value)}
                          suffix={
                            <PlusOutlined
                              onClick={handleAddSystemType}
                              style={{ color: "#e11d48", cursor: "pointer" }}
                            />
                          }
                        />

                        <Button
                          type="primary"
                          icon={<CheckOutlined />}
                          loading={addingType}
                          onClick={handleAddSystemType}
                        />
                      </div>
                    </>
                  </>
                )}
              />
            </Form.Item>
          </Col>

          {[
            "location_on_board",
            "make_type",
            "year_of_installation",
            "refrigerant_type",
            "refrigerant_quantity_kgs",
            "oil_quantity",
          ].map((f) => (
            <Col span={12} key={f}>
              <Form.Item 
                label={f.replaceAll("_", " ")} 
                name={f} 
                required
                rules={[
                  { required: true, message: `The ${f.replaceAll("_", " ")} field is required.` },
                  ["year_of_installation", "refrigerant_quantity_kgs", "oil_quantity"].includes(f) && {
                    pattern: /^[0-9]+(\.[0-9]+)?$/,
                    message: "Please enter a valid number",
                  },
                ].filter(Boolean)}
              >
                <Input disabled={readOnly} />
              </Form.Item>
            </Col>
          ))}
        </Row>

        <Button type="primary" htmlType="submit" disabled={readOnly}>
          Save & Continue
        </Button>
      </Form>
    </div>
  );
};

export default OdsEquipment;
